import type * as CSS from 'csstype'
import type React from 'react'
import type { ComponentPropsWithoutRef, PropsWithChildren } from 'react'
import type { RuleSet } from 'styled-components'
import type { TokenFunction } from '../../styling/get-token'
import type { PartialPrefix } from '../../typescript'
import { css, styled } from 'styled-components'
import { withStyleModifiers } from '../../typescript'

interface SpaceBetweenProps extends ComponentPropsWithoutRef<'div'> {
  direction: 'x' | 'y'
  spacing?: string | TokenFunction
  /** CSS property: 'align-items' */
  alignItems?: CSS.Property.AlignItems
  /** CSS property: 'justify-content' */
  justifyContent?: CSS.Property.JustifyContent
  padding?: string | TokenFunction
}
type SpaceBetweenStyledProps = PartialPrefix<
  SpaceBetweenProps,
  'direction' | 'spacing' | 'alignItems' | 'justifyContent' | 'padding'
>

const StyledSpaceBetween = styled.div<SpaceBetweenStyledProps>`
  ${(props) => {
    switch (props.$direction) {
      case 'x':
        return css`
          flex-direction: row;
          > * ~ * {
            margin-left: ${props.$spacing ?? 'initial'} !important;
          }
        `
      case 'y':
        return css`
          flex-direction: column;
          > * ~ * {
            margin-top: ${props.$spacing ?? 'initial'} !important;
          }
        `
    }
  }}
  display: flex;
  align-items: ${props => props.$alignItems};
  justify-content: ${props => props.$justifyContent};
  padding: ${props => props.$padding};
`

function SpaceBetweenComponent({
  alignItems,
  direction,
  spacing,
  justifyContent,
  padding,
  children,
  ...attributes
}: PropsWithChildren<SpaceBetweenProps>): React.JSX.Element {
  return (
    <StyledSpaceBetween
      $spacing={spacing}
      $direction={direction}
      $alignItems={alignItems}
      $justifyContent={justifyContent}
      $padding={padding}
      {...attributes}
    >
      {children}
    </StyledSpaceBetween>
  )
}

SpaceBetweenComponent.displayName = 'SpaceBetween'

/**
 * Generates a CSS rule set for spacing between elements, based on the direction. This can be triggered by the parent component, to apply different spacing based on responsive breakpoints.
 *
 * @param spacing - The spacing between elements.
 * @param direction - The direction of the spacing. Defaults to 'x'.
 * @returns - The generated CSS rule set.
 *
 * @example
 * ```css
 * // Usage:
 * ${SpaceBetween} {
 *  ${SpaceBetween.modAlignment(tk('spacing_md'))};
 * }
 * ```
 */
function modAlignment(spacing: SpaceBetweenProps['spacing'], direction: SpaceBetweenProps['direction'] = 'x'): RuleSet<object> {
  return css`
  ${() => {
    return css`
      > * ~ * {
        ${direction === 'x' ? 'margin-left' : 'margin-top'}: ${spacing} !important;
      }
    `
  }}
`
}

/**
 * @deprecated Avoid using this component. Use flexbox instead
 */
export const SpaceBetween = withStyleModifiers(styled(SpaceBetweenComponent)``, {
  modAlignment,
})
